import React from 'react';
import { Loader2 } from 'lucide-react';
import { useAdminTheme, tc } from './theme';

/**
 * Shared building blocks used by every legacy CRUD admin page (Blog, Leads,
 * Pricing, Regulations, etc.). They were originally dark-only; now they read
 * `useAdminTheme()` and switch their surface / text colours so the same 12
 * legacy pages render correctly in both light and dark admin themes.
 */

export function AdminCard({ title, subtitle, children, action, className = '' }) {
  const { theme } = useAdminTheme();
  return (
    <section className={`rounded-2xl p-5 md:p-6 ${tc.cardBg(theme)} ${className}`}>
      {(title || action) && (
        <header className="flex items-start justify-between gap-4 mb-4">
          <div>
            {title && <h2 className={`text-lg font-bold ${tc.textPrimary(theme)}`}>{title}</h2>}
            {subtitle && <p className={`text-sm mt-0.5 ${tc.textMuted(theme)}`}>{subtitle}</p>}
          </div>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

export function PageHeader({ title, subtitle, action }) {
  const { theme } = useAdminTheme();
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
      <div>
        <h1 className={`text-2xl md:text-3xl font-extrabold ${tc.textPrimary(theme)}`}>{title}</h1>
        {subtitle && <p className={`text-sm mt-1 ${tc.textMuted(theme)}`}>{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function StatCard({ label, value, sub, accent = 'violet' }) {
  const { theme } = useAdminTheme();
  // The gradient stat cards stay vivid in both themes — they're an
  // intentional accent and look good on light + dark surfaces.
  const tone = {
    violet: 'from-violet-500/30 to-fuchsia-500/10 border-violet-500/30',
    emerald: 'from-emerald-500/25 to-teal-500/10 border-emerald-500/30',
    amber: 'from-amber-500/25 to-orange-500/10 border-amber-500/30',
    rose: 'from-rose-500/25 to-pink-500/10 border-rose-500/30',
    sky: 'from-sky-500/25 to-blue-500/10 border-sky-500/30',
  }[accent];
  return (
    <div className={`rounded-2xl p-4 bg-gradient-to-br ${tone} border backdrop-blur`}>
      <p className={`text-[11px] uppercase tracking-widest ${theme === 'light' ? 'text-slate-700 font-semibold' : 'text-slate-300'}`}>{label}</p>
      <p className={`text-3xl font-extrabold mt-1 ${theme === 'light' ? 'text-slate-900' : 'text-white'}`}>{value}</p>
      {sub && <p className={`text-xs mt-1 ${theme === 'light' ? 'text-slate-700/80' : 'text-slate-300/70'}`}>{sub}</p>}
    </div>
  );
}

export function Btn({ children, onClick, type = 'button', variant = 'primary', disabled = false, size = 'md', className = '', testId }) {
  const { theme } = useAdminTheme();
  const sizeCls = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-4 py-2 text-sm';
  const variants = {
    primary: 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg shadow-fuchsia-500/20 hover:opacity-90',
    ghost: theme === 'light'
      ? 'bg-slate-100 text-slate-700 hover:bg-slate-200 border border-slate-200'
      : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10',
    danger: theme === 'light'
      ? 'bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100'
      : 'bg-rose-500/15 text-rose-200 border border-rose-500/40 hover:bg-rose-500/25',
    success: theme === 'light'
      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
      : 'bg-emerald-500/15 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/25',
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className={`inline-flex items-center gap-2 rounded-lg font-semibold transition-all disabled:opacity-50 ${sizeCls} ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
}

export function Input({ label, hint, className = '', ...rest }) {
  const { theme } = useAdminTheme();
  return (
    <label className="block">
      {label && <span className={`text-xs font-semibold mb-1.5 block ${tc.textMuted(theme)}`}>{label}</span>}
      <input
        {...rest}
        className={`w-full px-3 py-2 rounded-lg outline-none text-sm transition-all ${tc.inputBg(theme)} ${className}`}
      />
      {hint && <span className={`text-[11px] mt-1 block ${tc.textDim(theme)}`}>{hint}</span>}
    </label>
  );
}

export function Textarea({ label, hint, className = '', ...rest }) {
  const { theme } = useAdminTheme();
  return (
    <label className="block">
      {label && <span className={`text-xs font-semibold mb-1.5 block ${tc.textMuted(theme)}`}>{label}</span>}
      <textarea
        {...rest}
        className={`w-full px-3 py-2 rounded-lg outline-none text-sm resize-none transition-all ${tc.inputBg(theme)} ${className}`}
      />
      {hint && <span className={`text-[11px] mt-1 block ${tc.textDim(theme)}`}>{hint}</span>}
    </label>
  );
}

export function Select({ label, children, className = '', ...rest }) {
  const { theme } = useAdminTheme();
  return (
    <label className="block">
      {label && <span className={`text-xs font-semibold mb-1.5 block ${tc.textMuted(theme)}`}>{label}</span>}
      <select
        {...rest}
        className={`w-full px-3 py-2 rounded-lg outline-none text-sm transition-all ${tc.inputBg(theme)} ${className}`}
      >
        {children}
      </select>
    </label>
  );
}

export function StatusBadge({ status }) {
  const { theme } = useAdminTheme();
  // Status colours kept distinct in both themes — they're an accent meant
  // to grab the eye at a glance, so they stay vivid on either surface.
  const darkMap = {
    new: 'bg-sky-500/20 text-sky-200 border-sky-500/40',
    pending: 'bg-amber-500/20 text-amber-200 border-amber-500/40',
    contacted: 'bg-violet-500/20 text-violet-200 border-violet-500/40',
    approved: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40',
    shipped: 'bg-teal-500/20 text-teal-200 border-teal-500/40',
    closed: 'bg-slate-500/20 text-slate-300 border-slate-500/40',
    cancelled: 'bg-rose-500/20 text-rose-200 border-rose-500/40',
  };
  const lightMap = {
    new: 'bg-sky-50 text-sky-800 border-sky-200',
    pending: 'bg-amber-50 text-amber-800 border-amber-200',
    contacted: 'bg-violet-50 text-violet-800 border-violet-200',
    approved: 'bg-emerald-50 text-emerald-800 border-emerald-200',
    shipped: 'bg-teal-50 text-teal-800 border-teal-200',
    closed: 'bg-slate-100 text-slate-700 border-slate-200',
    cancelled: 'bg-rose-50 text-rose-800 border-rose-200',
  };
  const map = theme === 'light' ? lightMap : darkMap;
  const fallback = theme === 'light' ? 'bg-slate-100 text-slate-700 border-slate-200' : 'bg-slate-500/20 text-slate-300 border-slate-500/40';
  const cls = map[status] || fallback;
  return <span className={`inline-flex text-[11px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-wider ${cls}`}>{status || '—'}</span>;
}

export function Loading() {
  const { theme } = useAdminTheme();
  return (
    <div className={`flex items-center justify-center py-20 ${tc.textMuted(theme)}`}>
      <Loader2 size={20} className="animate-spin mr-2" /> Loading…
    </div>
  );
}

export function Empty({ children }) {
  const { theme } = useAdminTheme();
  return <div className={`py-16 text-center text-sm ${tc.textMuted(theme)}`}>{children || 'Nothing here yet.'}</div>;
}

export function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
